import { useState } from "react";
import type {
  AccountKind,
  CreateAccountInput,
  CreateManualTransactionInput,
  CreateMerchantRuleInput,
  MerchantRule,
} from "@money/shared";
import {
  useAccounts,
  useCreateAccount,
  useDeleteAccount,
  useUpdateAccount,
} from "../api/accounts";
import {
  useCreateManualTransaction,
  useDeletedTransactions,
  useRestoreTransaction,
} from "../api/transactions";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
} from "../api/categories";
import {
  useMerchantRules,
  useCreateMerchantRule,
  useUpdateMerchantRule,
  useDeleteMerchantRule,
} from "../api/merchantRules";
import { useProfile, useUpdateProfile } from "../api/profile";
import { useAlerts, useCreateAlert, useUpdateAlert, useDeleteAlert } from "../api/alerts";
import type { AlertType, CreateAlertInput } from "@money/shared";
import {
  useSimplefinStatus,
  useSimplefinConnect,
  useSimplefinDisconnect,
  useSimplefinSync,
  useSimplefinBackfill,
} from "../api/simplefin";
import { AccountCard } from "../components/AccountCard";
import { Loading, ErrorState } from "../components/States";
import { useToast } from "../components/Toast";
import { dateInputToTs, dateInputValue, formatCents, formatDate } from "../lib/format";

const KIND_OPTIONS: { value: AccountKind; label: string }[] = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
  { value: "credit", label: "Credit card" },
  { value: "investment_taxable", label: "Investment — taxable" },
  { value: "investment_traditional", label: "Investment — traditional IRA / 401k" },
  { value: "investment_roth", label: "Investment — Roth" },
  { value: "manual", label: "Other / manual" },
];

export function Settings() {
  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Profile</h2>
        <ProfileSettings />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Bank connection (SimpleFIN)</h2>
        <BankConnection />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Add manual account</h2>
        <AddAccountForm />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Add manual transaction</h2>
        <AddTransactionForm />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Existing accounts</h2>
        <AccountsList />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Categories</h2>
        <CategoriesManager />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Merchant rules</h2>
        <MerchantRulesManager />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Alerts</h2>
        <AlertsManager />
      </section>

      <section>
        <h2 className="text-sm font-medium text-slate-300 mb-2">Recently deleted</h2>
        <DeletedTransactionsManager />
      </section>
    </div>
  );
}

function DeletedTransactionsManager() {
  const deleted = useDeletedTransactions();
  const restore = useRestoreTransaction();
  const toast = useToast();
  const list = deleted.data?.transactions ?? [];

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <p className="text-xs text-slate-500">
        Deleted transactions are kept here so you can restore them — nothing is
        permanently lost, including data synced from SimpleFIN.
      </p>
      {deleted.isPending && <Loading />}
      {deleted.isError && <ErrorState onRetry={() => deleted.refetch()} />}
      {!deleted.isPending && !deleted.isError && list.length === 0 && (
        <div className="text-slate-500 text-sm">Nothing deleted.</div>
      )}
      <div className="space-y-1">
        {list.map((tx) => (
          <div
            key={tx.id}
            className="flex items-center justify-between border-b border-slate-800 py-2 last:border-b-0 text-sm gap-3"
          >
            <div className="min-w-0">
              <div className="font-medium truncate">{tx.display_name ?? tx.merchant_raw}</div>
              <div className="text-xs text-slate-500">
                {formatDate(tx.posted_at)} · {formatCents(tx.amount_cents)}
                {tx.category ? ` · ${tx.category}` : ""}
              </div>
            </div>
            <button
              onClick={() =>
                restore.mutate(tx.id, { onSuccess: () => toast("Transaction restored") })
              }
              className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap"
            >
              Restore
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const ALERT_TYPES: { value: AlertType; label: string }[] = [
  { value: "large_transaction", label: "Large transaction" },
  { value: "low_balance", label: "Low account balance" },
  { value: "category_budget", label: "Category over budget" },
];

function AlertsManager() {
  const alerts = useAlerts();
  const create = useCreateAlert();
  const update = useUpdateAlert();
  const del = useDeleteAlert();
  const toast = useToast();

  const [type, setType] = useState<AlertType>("large_transaction");
  const [amount, setAmount] = useState("500");
  const [category, setCategory] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setError("Enter an amount.");
      return;
    }
    let config: Record<string, unknown>;
    if (type === "large_transaction") config = { threshold_cents: cents };
    else if (type === "low_balance") config = { threshold_cents: cents };
    else {
      if (!category.trim()) {
        setError("Enter a category.");
        return;
      }
      config = { category: category.trim(), monthly_limit_cents: cents };
    }
    const input: CreateAlertInput = { alert_type: type, config };
    try {
      await create.mutateAsync(input);
      setCategory("");
      toast("Alert added");
    } catch (err) {
      setError(String(err));
    }
  };

  const list = alerts.data?.alerts ?? [];

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <form onSubmit={onAdd} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Alert type">
          <select value={type} onChange={(e) => setType(e.target.value as AlertType)} className={inputCls}>
            {ALERT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>
        {type === "category_budget" && (
          <Field label="Category">
            <input value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} />
          </Field>
        )}
        <Field label={type === "category_budget" ? "Monthly limit ($)" : "Threshold ($)"}>
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
        </Field>
        <div className="flex items-end">
          <button type="submit" disabled={create.isPending} className={btnCls}>
            {create.isPending ? "Adding…" : "Add alert"}
          </button>
        </div>
      </form>
      {error && <div className="text-xs text-rose-400">{error}</div>}

      <div className="space-y-1">
        {alerts.isPending && <Loading />}
        {alerts.isError && <ErrorState onRetry={() => alerts.refetch()} />}
        {!alerts.isPending && !alerts.isError && list.length === 0 && (
          <div className="text-slate-500 text-sm">No alerts configured.</div>
        )}
        {list.map((a) => {
          let cfg: Record<string, unknown> = {};
          try {
            cfg = JSON.parse(a.config);
          } catch {
            /* ignore */
          }
          const label = ALERT_TYPES.find((t) => t.value === a.alert_type)?.label ?? a.alert_type;
          return (
            <div key={a.id} className="flex items-center justify-between border-b border-slate-800 py-2 last:border-b-0 text-sm">
              <span className={a.enabled ? "" : "text-slate-500"}>
                {label} · {describeConfig(a.alert_type, cfg)}
                {a.enabled ? "" : " (off)"}
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => update.mutate({ id: a.id, patch: { enabled: a.enabled ? 0 : 1 } })}
                  className="text-xs text-slate-400 hover:text-slate-200"
                >
                  {a.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  onClick={() => del.mutate(a.id, { onSuccess: () => toast("Alert removed") })}
                  className="text-xs text-slate-500 hover:text-rose-400"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function describeConfig(type: string, cfg: Record<string, unknown>): string {
  const d = (c: unknown) => (typeof c === "number" ? `$${(c / 100).toFixed(0)}` : "?");
  if (type === "category_budget") return `${cfg.category ?? "?"} > ${d(cfg.monthly_limit_cents)}/mo`;
  if (type === "low_balance") return `below ${d(cfg.threshold_cents)}`;
  return `≥ ${d(cfg.threshold_cents)}`;
}

function CategoriesManager() {
  const categories = useCategories();
  const create = useCreateCategory();
  const del = useDeleteCategory();
  const toast = useToast();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ name: name.trim() });
      setName("");
      toast("Category added");
    } catch (err) {
      setError(String(err));
    }
  };

  const list = categories.data?.categories ?? [];

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <form onSubmit={onAdd} className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New category (e.g. Groceries)"
          className={`${inputCls} flex-1`}
        />
        <button type="submit" disabled={create.isPending} className={btnCls}>
          {create.isPending ? "Adding…" : "Add"}
        </button>
      </form>
      {error && <div className="text-xs text-rose-400">{error}</div>}
      {categories.isPending && <Loading />}
      {categories.isError && <ErrorState onRetry={() => categories.refetch()} />}
      {!categories.isPending && !categories.isError && list.length === 0 && (
        <div className="text-slate-500 text-sm">No categories yet.</div>
      )}
      <div className="flex flex-wrap gap-2">
        {list.map((c) => (
          <span
            key={c.id}
            className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1 text-sm"
          >
            {c.name}
            <button
              onClick={() => del.mutate(c.id, { onSuccess: () => toast("Category deleted") })}
              aria-label={`Delete ${c.name}`}
              className="text-slate-500 hover:text-rose-400 leading-none"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function MerchantRulesManager() {
  const rules = useMerchantRules();
  const categories = useCategories();
  const create = useCreateMerchantRule();
  const update = useUpdateMerchantRule();
  const del = useDeleteMerchantRule();
  const toast = useToast();

  const [pattern, setPattern] = useState("");
  const [category, setCategory] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [skipAi, setSkipAi] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!pattern.trim()) {
      setError("Enter a merchant pattern.");
      return;
    }
    if (!category && !skipAi && !displayName.trim()) {
      setError("Set a category, a display name, mark skip-AI, or a combination.");
      return;
    }
    try {
      const input: CreateMerchantRuleInput = {
        pattern: pattern.trim(),
        skip_ai: skipAi ? 1 : 0,
      };
      if (category) input.category = category;
      if (displayName.trim()) input.display_name = displayName.trim();
      await create.mutateAsync(input);
      setPattern("");
      setCategory("");
      setDisplayName("");
      setSkipAi(false);
      toast("Rule added");
    } catch (err) {
      setError(String(err));
    }
  };

  const cats = categories.data?.categories ?? [];
  const list = rules.data?.rules ?? [];

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <p className="text-xs text-slate-500">
        A rule matches transactions whose merchant contains the pattern
        (case-insensitive). It can assign a category, rename the merchant's
        displayed title, keep matches out of AI, or any combination. When several
        renames match a payee, the longest pattern wins.
      </p>
      <form onSubmit={onAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Merchant contains">
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="e.g. NETFLIX"
            className={inputCls}
          />
        </Field>
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={inputCls}
          >
            <option value="">— none —</option>
            {cats.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Display name (rename)">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Amazon"
            className={inputCls}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={skipAi}
            onChange={(e) => setSkipAi(e.target.checked)}
          />
          <span>Hide matches from AI (skip categorization + embeddings)</span>
        </label>
        <div className="sm:col-span-2 flex items-center justify-between gap-3">
          {error && <div className="text-xs text-rose-400">{error}</div>}
          <button type="submit" disabled={create.isPending} className={btnCls}>
            {create.isPending ? "Adding…" : "Add rule"}
          </button>
        </div>
      </form>

      {rules.isPending && <Loading />}
      {rules.isError && <ErrorState onRetry={() => rules.refetch()} />}
      {!rules.isPending && !rules.isError && list.length === 0 && (
        <div className="text-slate-500 text-sm">No rules yet.</div>
      )}
      <div className="space-y-1">
        {list.map((r) => (
          <MerchantRuleRow
            key={r.id}
            rule={r}
            onSaveName={(display_name) =>
              update.mutate(
                { id: r.id, patch: { display_name } },
                { onSuccess: () => toast("Rule updated") },
              )
            }
            onDelete={() => del.mutate(r.id, { onSuccess: () => toast("Rule deleted") })}
          />
        ))}
      </div>
    </div>
  );
}

function MerchantRuleRow({
  rule,
  onSaveName,
  onDelete,
}: {
  rule: MerchantRule;
  onSaveName: (displayName: string) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(rule.display_name ?? "");
  const dirty = name.trim() !== (rule.display_name ?? "");

  return (
    <div className="flex items-center justify-between border-b border-slate-800 py-2 last:border-b-0 text-sm gap-3">
      <div className="min-w-0">
        <div>
          <span className="font-mono text-slate-200">{rule.pattern}</span>
          <span className="text-slate-500">
            {" → "}
            {rule.category || "(no category)"}
            {rule.skip_ai === 1 ? " · skip-AI" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="display name…"
            className="rounded border border-slate-700 bg-slate-950/40 px-2 py-0.5 text-xs placeholder-slate-600"
          />
          {dirty && (
            <button
              onClick={() => onSaveName(name.trim())}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Save
            </button>
          )}
        </div>
      </div>
      <button
        onClick={onDelete}
        aria-label="Delete rule"
        className="text-slate-500 hover:text-rose-400 text-lg leading-none px-1 shrink-0"
      >
        ×
      </button>
    </div>
  );
}

function ProfileSettings() {
  const profile = useProfile();
  const update = useUpdateProfile();
  const p = profile.data;

  const [year, setYear] = useState("");
  const [income, setIncome] = useState("");
  const [k401, setK401] = useState("");
  const [matchCap, setMatchCap] = useState("");
  const [filing, setFiling] = useState("");
  const [saved, setSaved] = useState(false);

  const dollars = (cents: number | null | undefined) => (cents != null ? String(cents / 100) : "");
  const toCents = (s: string): number | null => (s.trim() ? Math.round(parseFloat(s) * 100) : null);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(false);
    await update.mutateAsync({
      birth_year: year.trim() ? Number(year.trim()) : (p?.birth_year ?? null),
      annual_income_cents: income.trim() ? toCents(income) : (p?.annual_income_cents ?? null),
      retirement_contrib_ytd_cents: k401.trim() ? toCents(k401) : (p?.retirement_contrib_ytd_cents ?? null),
      employer_match_cap_cents: matchCap.trim() ? toCents(matchCap) : (p?.employer_match_cap_cents ?? null),
      filing_status: (filing || p?.filing_status || null) as "single" | "married" | null,
    });
    setSaved(true);
    setYear("");
    setIncome("");
    setK401("");
    setMatchCap("");
    setFiling("");
  };

  return (
    <form onSubmit={onSave} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <p className="text-xs text-slate-500">
        Birth year drives FIRE age projections. Income / 401k / match / filing status drive the
        precise tax-opportunity observations on the Patterns page. Leave a box blank to keep its
        current value.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <ProfileField label="Birth year" value={year} setValue={setYear} placeholder={p?.birth_year != null ? String(p.birth_year) : "1990"} />
        <ProfileField label="Annual income ($)" value={income} setValue={setIncome} placeholder={dollars(p?.annual_income_cents) || "—"} />
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase tracking-wide text-slate-500">Filing status</span>
          <select value={filing} onChange={(e) => setFiling(e.target.value)} className={inputCls}>
            <option value="">{p?.filing_status ?? "—"}</option>
            <option value="single">single</option>
            <option value="married">married</option>
          </select>
        </label>
        <ProfileField label="401k YTD ($)" value={k401} setValue={setK401} placeholder={dollars(p?.retirement_contrib_ytd_cents) || "—"} />
        <ProfileField label="Employer match cap ($)" value={matchCap} setValue={setMatchCap} placeholder={dollars(p?.employer_match_cap_cents) || "—"} />
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={update.isPending} className={btnCls}>
          {update.isPending ? "Saving…" : "Save"}
        </button>
        {saved && <span className="text-xs text-emerald-400">Saved.</span>}
      </div>
    </form>
  );
}

function ProfileField({
  label,
  value,
  setValue,
  placeholder,
}: {
  label: string;
  value: string;
  setValue: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} className={inputCls} />
    </label>
  );
}

function BankConnection() {
  const status = useSimplefinStatus();
  const connect = useSimplefinConnect();
  const disconnect = useSimplefinDisconnect();
  const sync = useSimplefinSync();
  const backfill = useSimplefinBackfill();

  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [backfillDays, setBackfillDays] = useState("365");

  if (status.isPending) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <Loading />
      </div>
    );
  }
  if (status.isError) {
    return <ErrorState onRetry={() => status.refetch()} />;
  }

  const connected = status.data?.connected ?? false;

  const onConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token.trim()) {
      setError("Paste a Setup Token first.");
      return;
    }
    try {
      await connect.mutateAsync({ setup_token: token.trim() });
      setToken("");
    } catch (err) {
      setError(connectErrorMessage(err));
    }
  };

  const onDisconnect = async () => {
    if (!confirm("Disconnect SimpleFIN? Imported data stays; future syncs stop.")) {
      return;
    }
    setError(null);
    try {
      await disconnect.mutateAsync();
    } catch (err) {
      setError(String(err));
    }
  };

  const onSync = async () => {
    setError(null);
    setSyncMsg(null);
    try {
      const r = await sync.mutateAsync();
      setSyncMsg(
        `Synced ${r.accounts_synced} account(s), ${r.transactions_added} new transaction(s).`,
      );
    } catch (err) {
      setError(syncErrorMessage(err));
    }
  };

  const onBackfill = async () => {
    setError(null);
    setSyncMsg(null);
    try {
      const r = await backfill.mutateAsync({ days: Number(backfillDays) });
      setSyncMsg(
        `Backfilled ${r.accounts_synced} account(s), ${r.transactions_added} new transaction(s).`,
      );
    } catch (err) {
      setError(syncErrorMessage(err));
    }
  };

  if (connected) {
    const d = status.data!;
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          <span className="text-sm font-medium">Connected</span>
        </div>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-slate-500">Connected</dt>
          <dd>{d.connected_at ? formatDate(d.connected_at) : "—"}</dd>
          <dt className="text-slate-500">Last sync</dt>
          <dd>{d.last_successful_pull ? formatDate(d.last_successful_pull) : "Never"}</dd>
          {d.last_error && (
            <>
              <dt className="text-slate-500">Last error</dt>
              <dd className="text-rose-400">
                {d.last_error}
                {d.last_error_at ? ` (${formatDate(d.last_error_at)})` : ""}
              </dd>
            </>
          )}
        </dl>
        {d.last_warnings && d.last_warnings.length > 0 && (
          <div className="rounded border border-amber-700/60 bg-amber-950/30 p-2 text-xs text-amber-100/90 space-y-1">
            <div className="font-medium text-amber-200">
              SimpleFIN reported issues with some accounts:
            </div>
            <ul className="list-disc pl-4">
              {d.last_warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
            <div className="text-amber-100/70">
              Affected accounts may be missing recent transactions until the issue
              (often a re-login at your bank) is resolved in the SimpleFIN Bridge.
            </div>
          </div>
        )}
        {syncMsg && <div className="text-xs text-emerald-400">{syncMsg}</div>}
        {error && <div className="text-xs text-rose-400">{error}</div>}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onSync}
            disabled={sync.isPending || backfill.isPending}
            className={btnCls}
          >
            {sync.isPending ? "Syncing…" : "Sync now"}
          </button>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnect.isPending}
            className="rounded border border-slate-700 hover:border-rose-500 hover:text-rose-300 text-sm px-4 py-2 transition disabled:opacity-50"
          >
            {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
        <div className="border-t border-slate-800 pt-3 space-y-2">
          <p className="text-xs text-slate-500">
            Re-sync a wider history window. Use this if an account is missing older
            transactions — SimpleFIN backfills history after you first connect, and
            normal syncs only pull recent days.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={backfillDays}
              onChange={(e) => setBackfillDays(e.target.value)}
              disabled={backfill.isPending}
              className={inputCls + " max-w-[12rem]"}
            >
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last year</option>
              <option value="730">Last 2 years</option>
            </select>
            <button
              type="button"
              onClick={onBackfill}
              disabled={sync.isPending || backfill.isPending}
              className="rounded border border-slate-700 hover:border-blue-500 hover:text-blue-300 text-sm px-4 py-2 transition disabled:opacity-50"
            >
              {backfill.isPending ? "Backfilling…" : "Re-sync history"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onConnect}
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3"
    >
      <p className="text-sm text-slate-400">
        Paste a Setup Token from the{" "}
        <a
          href="https://beta-bridge.simplefin.org/"
          target="_blank"
          rel="noreferrer"
          className="text-blue-400 hover:underline"
        >
          SimpleFIN Bridge
        </a>
        . A token can be claimed only once — generate a fresh one if this fails.
      </p>
      <textarea
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Paste Setup Token…"
        rows={3}
        className={`${inputCls} w-full font-mono text-xs`}
      />
      {error && <div className="text-xs text-rose-400">{error}</div>}
      <button type="submit" disabled={connect.isPending} className={btnCls}>
        {connect.isPending ? "Connecting…" : "Connect"}
      </button>
    </form>
  );
}

function connectErrorMessage(err: unknown): string {
  const body = (err as { body?: { error?: string } } | undefined)?.body;
  switch (body?.error) {
    case "setup_token_required":
      return "Paste a Setup Token first.";
    case "invalid_setup_token":
      return "That doesn't look like a valid Setup Token.";
    case "claim_failed":
      return "Couldn't claim the token — it may already be used. Generate a fresh one.";
    case "invalid_access_url":
      return "SimpleFIN returned an unexpected response. Try a fresh token.";
    default:
      return "Connection failed. Try again with a fresh token.";
  }
}

function syncErrorMessage(err: unknown): string {
  const body = (err as { body?: { error?: string } } | undefined)?.body;
  switch (body?.error) {
    case "synced_recently":
      return "Synced recently — try again in a bit (limited to once per hour).";
    case "not_connected":
      return "Not connected. Add a Setup Token first.";
    case "auth_rejected":
      return "SimpleFIN rejected the stored credentials. Reconnect with a fresh Setup Token.";
    case "network_error":
      return "Couldn't reach SimpleFIN. Try again shortly.";
    default:
      return "Sync failed. Try again shortly.";
  }
}

function AddAccountForm() {
  const [institution, setInstitution] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [kind, setKind] = useState<AccountKind>("checking");
  const [balanceDollars, setBalanceDollars] = useState("0");
  const [error, setError] = useState<string | null>(null);

  const create = useCreateAccount();
  const toast = useToast();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const cents = Math.round(parseFloat(balanceDollars || "0") * 100);
    if (!Number.isFinite(cents)) {
      setError("Balance must be a number.");
      return;
    }
    const input: CreateAccountInput = {
      institution: institution.trim(),
      display_name: displayName.trim(),
      kind,
      balance_cents: cents,
    };
    try {
      await create.mutateAsync(input);
      setInstitution("");
      setDisplayName("");
      setBalanceDollars("0");
      toast("Account added");
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      <Field label="Institution">
        <input
          required
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          placeholder="Chase, NCSECU, Fidelity…"
          className={inputCls}
        />
      </Field>
      <Field label="Display name">
        <input
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Joint checking"
          className={inputCls}
        />
      </Field>
      <Field label="Kind">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as AccountKind)}
          className={inputCls}
        >
          {KIND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Starting balance (USD)">
        <input
          required
          inputMode="decimal"
          value={balanceDollars}
          onChange={(e) => setBalanceDollars(e.target.value)}
          className={inputCls}
        />
      </Field>
      <div className="sm:col-span-2 flex items-center justify-between gap-3">
        {error && <div className="text-xs text-rose-400">{error}</div>}
        <button
          type="submit"
          disabled={create.isPending}
          className={btnCls}
        >
          {create.isPending ? "Adding…" : "Add account"}
        </button>
      </div>
    </form>
  );
}

function AddTransactionForm() {
  const accounts = useAccounts();
  const create = useCreateManualTransaction();
  const toast = useToast();

  const [accountId, setAccountId] = useState("");
  const [postedDate, setPostedDate] = useState(dateInputValue(Date.now()));
  const [amountDollars, setAmountDollars] = useState("");
  const [merchant, setMerchant] = useState("");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const cents = Math.round(parseFloat(amountDollars || "0") * 100);
    if (!Number.isFinite(cents) || cents === 0) {
      setError("Amount required (use a negative number for spending).");
      return;
    }
    if (!accountId) {
      setError("Pick an account.");
      return;
    }
    const input: CreateManualTransactionInput = {
      account_id: accountId,
      posted_at: dateInputToTs(postedDate),
      amount_cents: cents,
      merchant_raw: merchant.trim(),
    };
    const trimmedNotes = notes.trim();
    if (trimmedNotes) input.description = trimmedNotes;
    const trimmedCategory = category.trim();
    if (trimmedCategory) input.category = trimmedCategory;
    try {
      await create.mutateAsync(input);
      setAmountDollars("");
      setMerchant("");
      setCategory("");
      setNotes("");
      toast("Transaction added");
    } catch (err) {
      setError(String(err));
    }
  };

  const list = accounts.data?.accounts ?? [];

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      <Field label="Account">
        <select
          required
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className={inputCls}
        >
          <option value="">Select…</option>
          {list.map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name} ({a.institution})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Date">
        <input
          required
          type="date"
          value={postedDate}
          onChange={(e) => setPostedDate(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Amount (USD, negative for spending)">
        <input
          required
          inputMode="decimal"
          placeholder="-42.00"
          value={amountDollars}
          onChange={(e) => setAmountDollars(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Merchant">
        <input
          required
          value={merchant}
          onChange={(e) => setMerchant(e.target.value)}
          placeholder="Coffee shop"
          className={inputCls}
        />
      </Field>
      <Field label="Category (optional)">
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Notes (optional)">
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={inputCls}
        />
      </Field>
      <div className="sm:col-span-2 flex items-center justify-between gap-3">
        {error && <div className="text-xs text-rose-400">{error}</div>}
        <button type="submit" disabled={create.isPending} className={btnCls}>
          {create.isPending ? "Adding…" : "Add transaction"}
        </button>
      </div>
    </form>
  );
}

function AccountsList() {
  const accounts = useAccounts();
  const del = useDeleteAccount();
  const update = useUpdateAccount();
  const toast = useToast();

  if (accounts.isPending) {
    return <Loading />;
  }
  if (accounts.isError) {
    return <ErrorState onRetry={() => accounts.refetch()} />;
  }
  if (!accounts.data || accounts.data.accounts.length === 0) {
    return <div className="text-slate-500 text-sm">No accounts yet.</div>;
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        Marking an account private keeps its transactions out of all AI
        categorization — nothing from it is ever sent to Claude or Voyage.
      </p>
      {accounts.data.accounts.map((a) => (
        <AccountCard
          key={a.id}
          account={a}
          busy={update.isPending}
          onTogglePrivate={(id, next) => update.mutate({ id, patch: { private: next } })}
          onDelete={(id) => {
            if (confirm(`Delete account "${a.display_name}"?`)) {
              del.mutate(id, { onSuccess: () => toast("Account deleted") });
            }
          }}
        />
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "rounded border border-slate-700 bg-slate-950/40 px-3 py-2 text-slate-100 placeholder-slate-600 focus:outline-none focus:border-slate-500";

const btnCls =
  "rounded bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-sm font-medium px-4 py-2 transition";
